import { useState } from 'react';
import { motion } from 'framer-motion';
import { Volume2 } from 'lucide-react';
import { WordEntry, speakWord } from '@/lib/index';
import { cn } from '@/lib/utils';

interface WordCardProps {
  word: WordEntry;
  showAll?: boolean;
  onMasteryChange?: (lang: 'zh' | 'jp' | 'en', level: 0 | 1 | 2) => void;
}

export function WordCard({ word, showAll = false, onMasteryChange }: WordCardProps) {
  const [activeLang, setActiveLang] = useState<'zh' | 'jp' | 'en' | null>(null);

  const handleSpeak = (text: string, lang: 'zh' | 'jp' | 'en') => {
    setActiveLang(lang);
    speakWord(text, lang);
    setTimeout(() => setActiveLang(null), 1200);
  };

  const handleMastery = (lang: 'zh' | 'jp' | 'en', level: 0 | 1 | 2) => {
    if (onMasteryChange) {
      onMasteryChange(lang, level);
    }
  };

  const langConfig = {
    zh: {
      flag: '🇨🇳',
      cardBg: 'bg-lang-zh-bg',
      border: 'border-lang-zh-border',
      activeBorder: 'border-lang-zh',
      textColor: 'text-lang-zh',
      activeRing: 'ring-lang-zh',
    },
    jp: {
      flag: '🇯🇵',
      cardBg: 'bg-lang-jp-bg',
      border: 'border-lang-jp-border',
      activeBorder: 'border-lang-jp',
      textColor: 'text-lang-jp',
      activeRing: 'ring-lang-jp',
    },
    en: {
      flag: '🇬🇧',
      cardBg: 'bg-lang-en-bg',
      border: 'border-lang-en-border',
      activeBorder: 'border-lang-en',
      textColor: 'text-lang-en',
      activeRing: 'ring-lang-en',
    },
  };

  const masteryButtons = [
    { level: 2 as const, label: '✨熟练', bg: 'bg-mastery-master-bg', text: 'text-mastery-master', border: 'border-mastery-master-border' },
    { level: 1 as const, label: '📖练习', bg: 'bg-mastery-familiar-bg', text: 'text-mastery-familiar', border: 'border-mastery-familiar-border' },
    { level: 0 as const, label: '❓加油', bg: 'bg-mastery-learning-bg', text: 'text-mastery-learning', border: 'border-mastery-learning-border' },
  ];

  const renderLangSection = (lang: 'zh' | 'jp' | 'en') => {
    const cfg = langConfig[lang];
    const isActive = activeLang === lang;
    const mainText = lang === 'zh' ? word.zh : lang === 'jp' ? word.jp : word.en;
    const subText = lang === 'zh' ? word.pinyin : lang === 'jp' ? word.kana : '';

    return (
      <div key={lang} className="space-y-2">
        <button
          onClick={() => handleSpeak(mainText, lang)}
          className={cn(
            'w-full rounded-2xl border-2 transition-all duration-200 active:scale-95 cursor-pointer p-5',
            cfg.cardBg,
            isActive ? [cfg.activeBorder, 'ring-2', cfg.activeRing, 'shadow-md'] : cfg.border
          )}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-3xl flex-shrink-0">{cfg.flag}</span>
              <div className="text-left min-w-0">
                <div className={cn('font-bold leading-tight text-3xl', cfg.textColor)}>
                  {mainText}
                </div>
                {subText && (
                  <div className={cn('font-medium opacity-80 text-base', cfg.textColor)}>
                    {subText}
                  </div>
                )}
              </div>
            </div>
            <Volume2
              className={cn('flex-shrink-0 w-6 h-6', cfg.textColor, isActive && 'animate-pulse')}
            />
          </div>
        </button>

        {onMasteryChange && (
          <div className="grid grid-cols-3 gap-2 px-1">
            {masteryButtons.map(({ level, label, bg, text, border }) => (
              <button
                key={level}
                onClick={() => handleMastery(lang, level)}
                className={cn(
                  'py-2.5 px-2 rounded-xl text-sm font-bold border-2 transition-all duration-150 active:scale-95',
                  bg,
                  text,
                  border
                )}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (word.isChar) {
    return (
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 280, damping: 22 }}
      >
        <div className="bg-card border-2 border-border rounded-3xl shadow-md overflow-hidden p-6">
          <div className="text-center mb-6">
            <div className="inline-block relative mb-4">
              <div
                className="relative flex items-center justify-center rounded-2xl bg-mastery-familiar-bg border-2 border-mastery-familiar-border"
                style={{
                  width: '160px',
                  height: '160px',
                  backgroundImage:
                    'linear-gradient(to right, rgba(252, 211, 77, 0.3) 1px, transparent 1px), linear-gradient(to bottom, rgba(252, 211, 77, 0.3) 1px, transparent 1px)',
                  backgroundSize: '50% 50%',
                }}
              >
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-full h-px bg-mastery-familiar opacity-40" />
                </div>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="h-full w-px bg-mastery-familiar opacity-40" />
                </div>
                <motion.button
                  onClick={() => handleSpeak(word.zh, 'zh')}
                  whileTap={{ scale: 0.95 }}
                  className={cn(
                    'relative z-10 font-bold text-foreground leading-none cursor-pointer select-none text-7xl',
                    activeLang === 'zh' && 'text-lang-zh'
                  )}
                  style={{ fontFamily: '"Noto Sans SC", sans-serif' }}
                >
                  {word.zh}
                </motion.button>
              </div>
            </div>

            {word.pinyin && (
              <div className="text-2xl font-bold text-lang-zh mb-2">{word.pinyin}</div>
            )}
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-muted rounded-full text-sm font-medium text-muted-foreground">
              汉字 · {word.emoji}
            </span>
          </div>

          <div className="space-y-3">
            {renderLangSection('zh')}
            {renderLangSection('jp')}
            {renderLangSection('en')}
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ scale: 0.92, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 280, damping: 22 }}
    >
      <div className="bg-card border-2 border-border rounded-3xl shadow-md overflow-hidden p-6">
        <div className="text-center mb-6">
          <motion.div
            initial={{ scale: 0.5, rotate: -10 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            className="text-8xl mb-4 leading-none select-none"
          >
            {word.emoji}
          </motion.div>
          <span className="inline-block px-4 py-1.5 bg-muted rounded-full text-sm font-medium text-muted-foreground">
            {word.category}
          </span>
        </div>

        <div className="space-y-3">
          {renderLangSection('zh')}
          {renderLangSection('jp')}
          {renderLangSection('en')}
        </div>
      </div>
    </motion.div>
  );
}